import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { api } from '../lib/api'
import { PageHeader, Card, Button, Input } from '../components/ui'

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!on)} style={{
      width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
      background: on ? '#3525cd' : '#e0e3e5', position: 'relative',
      transition: 'background 0.2s', flexShrink: 0,
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        position: 'absolute', top: 3, left: on ? 21 : 3,
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(25,28,30,0.2)',
      }} />
    </button>
  )
}

function StatusMsg({ msg }: { msg: { text: string; ok: boolean } | null }) {
  if (!msg) return null
  return (
    <div style={{ fontSize: 13, color: msg.ok ? '#059669' : '#ba1a1a', marginTop: 8, fontWeight: 500 }}>
      {msg.text}
    </div>
  )
}

type TabId = 'account' | 'notifications' | 'danger'
const TABS: { id: TabId; label: string }[] = [
  { id: 'account', label: 'Account' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'danger', label: 'Danger Zone' },
]

export function Settings() {
  const { user, logout, updateUser } = useAuth()
  const [tab, setTab] = useState<TabId>('account')

  // Profile
  const [name, setName] = useState(user?.name ?? '')
  const [email, setEmail] = useState(user?.email ?? '')
  const [profileMsg, setProfileMsg] = useState<{ text: string; ok: boolean } | null>(null)
  const [savingProfile, setSavingProfile] = useState(false)

  // Password
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [pwMsg, setPwMsg] = useState<{ text: string; ok: boolean } | null>(null)
  const [savingPw, setSavingPw] = useState(false)

  // Notifications
  const [notifs, setNotifs] = useState({
    digest: 'daily',
    spike: true,
    usage: true,
    weekly: true,
  })
  const [notifEmail, setNotifEmail] = useState(user?.email ?? '')
  const [notifMsg, setNotifMsg] = useState<{ text: string; ok: boolean } | null>(null)

  // Delete
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deletingAccount, setDeletingAccount] = useState(false)

  async function handleSaveProfile() {
    setSavingProfile(true)
    setProfileMsg(null)
    try {
      await updateUser({ name: name.trim(), email: email.trim() })
      setProfileMsg({ text: 'Changes saved.', ok: true })
    } catch (err: unknown) {
      setProfileMsg({ text: err instanceof Error ? err.message : 'Failed to save.', ok: false })
    } finally {
      setSavingProfile(false)
    }
  }

  async function handleChangePassword() {
    if (newPw !== confirmPw) {
      setPwMsg({ text: 'Passwords do not match.', ok: false })
      return
    }
    if (newPw.length < 8) {
      setPwMsg({ text: 'Password must be at least 8 characters.', ok: false })
      return
    }
    setSavingPw(true)
    setPwMsg(null)
    try {
      await api.user.changePassword({ currentPassword: currentPw, newPassword: newPw })
      setPwMsg({ text: 'Password updated.', ok: true })
      setCurrentPw('')
      setNewPw('')
      setConfirmPw('')
    } catch (err: unknown) {
      setPwMsg({ text: err instanceof Error ? err.message : 'Failed to update password.', ok: false })
    } finally {
      setSavingPw(false)
    }
  }

  function handleSaveNotifs() {
    setNotifMsg({ text: 'Notification preferences saved.', ok: true })
    setTimeout(() => setNotifMsg(null), 3000)
  }

  async function handleDeleteAccount() {
    if (deleteConfirm !== 'delete my account') return
    setDeletingAccount(true)
    try {
      await api.user.deleteAccount()
      await logout()
    } catch {
      setDeletingAccount(false)
    }
  }

  return (
    <div style={{ padding: '40px 48px', maxWidth: 700 }}>
      <PageHeader title="Settings" />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 28, background: '#eceef0', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding: '8px 20px', borderRadius: 8, border: 'none',
              background: tab === t.id ? '#ffffff' : 'transparent',
              color: tab === t.id
                ? (t.id === 'danger' ? '#ba1a1a' : '#3525cd')
                : '#464555',
              cursor: 'pointer', fontSize: 14, fontFamily: 'Inter, sans-serif',
              fontWeight: tab === t.id ? 600 : 400,
              boxShadow: tab === t.id ? '0 1px 4px rgba(25,28,30,0.08)' : 'none',
              transition: 'all 0.15s',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Account */}
      {tab === 'account' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#191c1e', fontFamily: 'Manrope, sans-serif' }}>Profile</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: 'rgba(53,37,205,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 700, color: '#3525cd',
              }}>
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 600, color: '#191c1e' }}>{user?.name}</div>
                <div style={{ fontSize: 13, color: '#777587' }}>{user?.email}</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              <Input label="Name" value={name} onChange={setName} />
              <Input label="Email" value={email} onChange={setEmail} type="email" />
            </div>
            <Button size="sm" onClick={handleSaveProfile} disabled={savingProfile}>
              {savingProfile ? 'Saving…' : 'Save changes'}
            </Button>
            <StatusMsg msg={profileMsg} />
          </Card>

          <Card>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#191c1e', fontFamily: 'Manrope, sans-serif' }}>Change Password</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              <Input label="Current password" value={currentPw} onChange={setCurrentPw} type="password" />
              <Input label="New password" value={newPw} onChange={setNewPw} type="password" />
              <Input label="Confirm new password" value={confirmPw} onChange={setConfirmPw} type="password" />
            </div>
            <Button size="sm" onClick={handleChangePassword} disabled={savingPw || !currentPw || !newPw || !confirmPw}>
              {savingPw ? 'Updating…' : 'Update password'}
            </Button>
            <StatusMsg msg={pwMsg} />
          </Card>
        </div>
      )}

      {/* Notifications */}
      {tab === 'notifications' && (
        <Card>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 20, color: '#191c1e', fontFamily: 'Manrope, sans-serif' }}>Notification Preferences</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {[
              { key: 'spike', label: 'Feedback spike alert', sub: 'When 10+ submissions in 1 hour' },
              { key: 'usage', label: 'Usage warning', sub: 'At 80% of plan limit' },
              { key: 'weekly', label: 'Weekly summary', sub: 'Every Monday morning' },
            ].map(({ key, label, sub }, i) => (
              <div key={key} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 12px',
                background: i % 2 === 0 ? 'transparent' : 'rgba(242,244,246,0.5)',
                borderRadius: 8,
              }}>
                <div>
                  <div style={{ fontSize: 14, color: '#191c1e', fontWeight: 500 }}>{label}</div>
                  <div style={{ fontSize: 12, color: '#777587', marginTop: 2 }}>{sub}</div>
                </div>
                <Toggle
                  on={notifs[key as keyof typeof notifs] as boolean}
                  onChange={v => setNotifs(prev => ({ ...prev, [key]: v }))}
                />
              </div>
            ))}

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 12px',
            }}>
              <div>
                <div style={{ fontSize: 14, color: '#191c1e', fontWeight: 500 }}>New feedback digest</div>
                <div style={{ fontSize: 12, color: '#777587', marginTop: 2 }}>How often to receive a summary</div>
              </div>
              <select value={notifs.digest} onChange={e => setNotifs(prev => ({ ...prev, digest: e.target.value }))}
                style={{ background: '#eceef0', border: 'none', color: '#191c1e', borderRadius: 8, padding: '6px 10px', fontSize: 13, fontFamily: 'Inter, sans-serif', outline: 'none', cursor: 'pointer' }}>
                <option value="realtime">Real-time</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="never">Never</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 13, color: '#777587', marginBottom: 8, fontWeight: 500 }}>Send notifications to</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                value={notifEmail}
                onChange={e => setNotifEmail(e.target.value)}
                style={{ flex: 1, background: '#eceef0', border: 'none', borderRadius: 8, padding: '9px 12px', color: '#191c1e', fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none' }}
              />
              <Button size="sm" onClick={handleSaveNotifs}>Save</Button>
            </div>
            <StatusMsg msg={notifMsg} />
          </div>
        </Card>
      )}

      {/* Danger Zone */}
      {tab === 'danger' && (
        <Card style={{ borderLeft: '3px solid #ba1a1a' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#ba1a1a', marginBottom: 16, fontFamily: 'Manrope, sans-serif' }}>Danger Zone</div>
          <div style={{ padding: '14px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#191c1e' }}>Delete Account</div>
                <div style={{ fontSize: 13, color: '#777587', marginTop: 4 }}>
                  Permanently deletes your account, all projects, and feedback. Cannot be undone.
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, color: '#777587', marginBottom: 8 }}>
                Type <span style={{ color: '#191c1e', fontFamily: 'monospace', background: '#eceef0', padding: '1px 6px', borderRadius: 4 }}>delete my account</span> to confirm
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  value={deleteConfirm}
                  onChange={e => setDeleteConfirm(e.target.value)}
                  placeholder="delete my account"
                  style={{ flex: 1, background: '#eceef0', border: 'none', borderRadius: 8, padding: '9px 12px', color: '#191c1e', fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none' }}
                />
                <Button
                  variant="danger"
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirm !== 'delete my account' || deletingAccount}
                >
                  {deletingAccount ? 'Deleting…' : 'Delete Account'}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
